import { useTranslation } from "react-i18next";
import { FlightNumberIcon, LocationPinIcon, TrainIcon } from "../../../../components/icons";
import { cn } from "../../../../utils/classnames";
import {
  FIELD_TYPE_DATE,
  FIELD_TYPE_FLIGHT,
  FIELD_TYPE_TIME,
  GROUP_AIRPORT,
  LOCATIONS_BY_GROUP,
  SCHEDULE_FIELD_TYPES,
  SERVICE_CONFIG,
  findLocation,
} from "../constants";
import { DateInput, Field, FieldLabel, SelectInput, TextAreaInput, TextInput, TimeInput } from "./fields";
import { NoteList } from "./layout";
import { PassengerCountField } from "./PassengerCountField";
import { ServiceOptionField } from "./ServiceOptionField";

const fieldId = (fieldName) => `meet-assist-${fieldName}`;

/** Grid for a row of fields: stacked on phones, side by side from `lg`. */
const FieldRow = ({ columnCount, children }) => (
  <div
    className={cn(
      "tw:grid tw:grid-cols-1 tw:gap-x-[10px] tw:gap-y-7",
      columnCount === 3 ? "tw:lg:grid-cols-3" : "tw:lg:grid-cols-2"
    )}
  >
    {children}
  </div>
);

/** Airport + terminal dropdowns, or station dropdown + train details. */
const LocationFields = ({ group, location, trainDetails, errors, onChangeLocation, onChangeTrainDetails }) => {
  const { t } = useTranslation();
  const isAirport = group === GROUP_AIRPORT;
  const selectedLocation = findLocation(group, location.placeId);

  const locationOptions = LOCATIONS_BY_GROUP[group].map((option) => ({
    value: option.placeId,
    label: t(`meetAssistInquiry.${option.labelKey}`),
  }));
  const terminalOptions = (selectedLocation ? selectedLocation.terminals : []).map((terminal) => ({
    value: terminal,
    label: t(`meetAssistInquiry.Terminals.${terminal}`),
  }));

  // A new airport has different terminals, so the old choice is cleared.
  const handleLocationChange = (placeId) => onChangeLocation({ placeId, terminal: "" });

  return (
    <div className={cn("tw:grid tw:grid-cols-1 tw:gap-x-[10px] tw:gap-y-7", isAirport ? "tw:lg:grid-cols-[572px_1fr]" : "tw:lg:grid-cols-2")}>
      <Field id={fieldId("location")} label={t(`meetAssistInquiry.Fields.locationName.${group}`)} isRequired errorKey={errors.locationName}>
        <SelectInput
          id={fieldId("location")}
          value={location.placeId}
          options={locationOptions}
          errorKey={errors.locationName}
          onChange={handleLocationChange}
        />
      </Field>
      {isAirport ? (
        <Field id={fieldId("terminal")} label={t("meetAssistInquiry.Fields.terminalName")} isRequired errorKey={errors.terminalName}>
          <SelectInput
            id={fieldId("terminal")}
            value={location.terminal}
            placeholder={t("meetAssistInquiry.Placeholders.SelectTerminal")}
            options={terminalOptions}
            errorKey={errors.terminalName}
            onChange={(terminal) => onChangeLocation({ ...location, terminal })}
          />
        </Field>
      ) : (
        <Field id={fieldId("trainDetails")} label={t("meetAssistInquiry.Fields.trainDetails")}>
          <TextInput
            id={fieldId("trainDetails")}
            value={trainDetails}
            placeholder={t("meetAssistInquiry.Placeholders.TrainDetails")}
            icon={<TrainIcon size={15} viewBox="-1 0 14 14" aria-hidden="true" />}
            onChange={onChangeTrainDetails}
          />
        </Field>
      )}
    </div>
  );
};

/** One flight-number / date / time input, chosen by the field's type. */
const ScheduleInput = ({ fieldName, value, errorKey, onChange }) => {
  const { t } = useTranslation();
  const id = fieldId(fieldName);

  switch (SCHEDULE_FIELD_TYPES[fieldName]) {
    case FIELD_TYPE_FLIGHT:
      return (
        <TextInput
          id={id}
          value={value}
          errorKey={errorKey}
          maxLength={20}
          placeholder={t("meetAssistInquiry.Placeholders.FlightNumber")}
          icon={<FlightNumberIcon size={22} aria-hidden="true" />}
          onChange={(text) => onChange(text.toUpperCase())}
        />
      );
    case FIELD_TYPE_DATE:
      return (
        <DateInput
          id={id}
          value={value}
          errorKey={errorKey}
          placeholder={t("meetAssistInquiry.Placeholders.SelectDate")}
          onChange={onChange}
        />
      );
    case FIELD_TYPE_TIME:
    default:
      return (
        <TimeInput
          id={id}
          value={value}
          errorKey={errorKey}
          caption={t("meetAssistInquiry.Placeholders.TimeCaption")}
          placeholder={t("meetAssistInquiry.Placeholders.SelectTime")}
          onChange={onChange}
        />
      );
  }
};

/** Where the guest sets off from before a departure, and when. */
const PriorLocationFields = ({ group, form, errors, updateField }) => {
  const { t } = useTranslation();
  return (
    <div className="tw:flex tw:flex-col tw:gap-y-3">
      <FieldRow columnCount={2}>
        <Field
          id={fieldId("priorLocation")}
          label={t(`meetAssistInquiry.Fields.priorLocation.${group}`)}
          hint={t(`meetAssistInquiry.Fields.priorLocationHint.${group}`)}
          isRequired
          errorKey={errors.priorLocation}
        >
          <TextInput
            id={fieldId("priorLocation")}
            value={form.priorLocation}
            errorKey={errors.priorLocation}
            placeholder={t("meetAssistInquiry.Placeholders.SelectLocation")}
            icon={<LocationPinIcon size={16} aria-hidden="true" />}
            onChange={(text) => updateField("priorLocation", text)}
          />
        </Field>
        <Field
          id={fieldId("timeLeaving")}
          label={t(`meetAssistInquiry.Fields.timeLeaving.${group}`)}
          hint={t(`meetAssistInquiry.Fields.timeLeavingHint.${group}`)}
          isRequired
          errorKey={errors.timeLeaving}
        >
          <ScheduleInput
            fieldName="timeLeaving"
            value={form.timeLeaving}
            errorKey={errors.timeLeaving}
            onChange={(time) => updateField("timeLeaving", time)}
          />
        </Field>
      </FieldRow>
      <NoteList
        className="tw:!px-4 tw:!py-3 tw:!gap-y-[3px] tw:text-[13px] tw:leading-[18.5px]"
        notes={t(`meetAssistInquiry.PriorLocationNotes.${group}`, { returnObjects: true })}
      />
    </div>
  );
};

/** Label for a schedule field; departure/meeting labels depend on the group. */
const getScheduleLabelKey = (fieldName) => `meetAssistInquiry.Fields.${fieldName}`;

/**
 * The first card: passengers, location, service option, schedule, special
 * requests and the disclaimer -- all driven by SERVICE_CONFIG.
 */
export const MeetAssistDetailsSection = ({ serviceKey, form, errors, sitePrices, selectedTier, updateField, actions }) => {
  const { t } = useTranslation();
  const serviceConfig = SERVICE_CONFIG[serviceKey];
  const { group } = serviceConfig;

  return (
    <>
      <PassengerCountField
        adults={form.adults}
        children={form.children}
        childAges={form.childAges}
        errors={errors}
        onChangeAdults={actions.setAdults}
        onChangeChildren={actions.setChildren}
        onChangeChildAge={actions.setChildAge}
      />

      <LocationFields
        group={group}
        location={form.locations[group]}
        trainDetails={form.trainDetails}
        errors={errors}
        onChangeLocation={(location) => actions.setLocation(group, location)}
        onChangeTrainDetails={(text) => updateField("trainDetails", text)}
      />

      <ServiceOptionField
        serviceKey={serviceKey}
        selectedTier={selectedTier}
        sitePrices={sitePrices}
        onSelectTier={(tier) => actions.setTier(serviceKey, tier)}
      />

      {serviceConfig.scheduleRows.map((rowFieldNames) => (
        <FieldRow key={rowFieldNames.join("-")} columnCount={rowFieldNames.length}>
          {rowFieldNames.map((fieldName) => (
            <Field key={fieldName} id={fieldId(fieldName)} label={t(getScheduleLabelKey(fieldName))} isRequired errorKey={errors[fieldName]}>
              <ScheduleInput
                fieldName={fieldName}
                value={form[fieldName]}
                errorKey={errors[fieldName]}
                onChange={(value) => updateField(fieldName, value)}
              />
            </Field>
          ))}
        </FieldRow>
      ))}

      {serviceConfig.hasPriorLocation && (
        <PriorLocationFields group={group} form={form} errors={errors} updateField={updateField} />
      )}

      <Field id={fieldId("specialRequests")} label={t("meetAssistInquiry.Fields.specialRequests")}>
        <TextAreaInput
          id={fieldId("specialRequests")}
          value={form.specialRequests}
          placeholder={t("meetAssistInquiry.Placeholders.SpecialRequests")}
          onChange={(text) => updateField("specialRequests", text)}
        />
      </Field>

      <div className="tw:flex tw:flex-col tw:gap-y-3">
        <FieldLabel label={t("meetAssistInquiry.Fields.disclaimer")} />
        <NoteList
          className="tw:!p-5 tw:text-[14px] tw:leading-[20px]"
          notes={serviceConfig.disclaimerKeys.map((key) => t(`meetAssistInquiry.Disclaimers.${key}`))}
        />
      </div>
    </>
  );
};
